import type { DocumentReading, ReadDocumentRequest } from '../domain/wallet';
import type {
  AttentionAction, Booking, BudgetAllocation, BudgetItem, ChecklistItem, Destination, DocumentRow, Enums, EventWithPeople, Expense,
  ExpenseShare, Group, GroupInvitation, GroupMember, ItineraryEvent, Profile, Settlement, TravelCredential, TravelDocProfile, Trip,
  TripBudget, TripMember, TripStop, UserSettings, Subgroup,
} from '../domain/model';
import type { ErrorCode } from '../domain/errors';
import type { ChatAction, ChatMessage, ChatRequest, ChatResponse } from '../domain/assistant';

export interface ConversationRow { id: string; trip_id: string | null; title: string; last_message_at: string; created_at: string }
export interface MemoryRow { id: string; trip_id: string | null; kind: string; text: string; status: string; created_at: string }

export interface Session { userId: string; email: string | null }

export interface CapabilityStatus { capability: string; configured: boolean; provider: string | null; coverage: string; freshness: string; userMessage: string }
export interface AppConfig {
  environment: string;
  limits: { maxUploadBytes: number; maxPdfPages: number };
  scanMode: string;
  capabilities: CapabilityStatus[];
  /** False when the Worker API itself is unreachable (e.g. frontend dev server only). */
  apiReachable: boolean;
}

export type MemberWithProfile<T> = T & { profile: Pick<Profile, 'id' | 'display_name' | 'avatar_color'> | null };

export interface TripBundle {
  trip: Trip;
  stops: TripStop[];
  members: MemberWithProfile<TripMember>[];
  subgroups: (Subgroup & { member_ids: string[] })[];
  events: EventWithPeople[];
  myRole: Enums['trip_role'] | null;
  myStatus: Enums['participation_status'] | null;
  group: Pick<Group, 'id' | 'name'> | null;
}

export interface GroupSummary extends Group { myRole: Enums['group_role']; myStatus: Enums['member_status']; memberCount: number }
export interface GroupBundle {
  group: Group;
  members: MemberWithProfile<GroupMember>[];
  invitations: GroupInvitation[];
  trips: Trip[];
  myRole: Enums['group_role'];
}

export interface StopInput { city: string; country_code?: string | null; timezone?: string | null; arrive_on?: string | null; depart_on?: string | null }
export interface NewTripInput {
  title: string; date_mode: Enums['date_mode']; start_date: string | null; end_date: string | null; date_note: string | null;
  origin_city: string | null; origin_timezone: string | null; reporting_currency: string; has_existing_bookings: boolean;
  group_id: string | null; stops: StopInput[];
}

export type EventInput = Partial<Omit<ItineraryEvent, 'id' | 'created_at' | 'updated_at' | 'version' | 'created_by'>> & { trip_id: string; title: string };

export interface BudgetBundle {
  budget: TripBudget | null;
  items: (BudgetItem & { allocations: BudgetAllocation[] })[];
  expenses: Expense[];
  shares: ExpenseShare[];
  settlements: Settlement[];
  myLimit: { limit_minor: number; currency: string } | null;
}

export interface ExpenseInput {
  tripId: string; amountMinor: number; currency: string; occurredOn: string; idempotencyKey: string; payerId?: string | null;
  description?: string | null; category?: Enums['cost_category'] | null; visibility: 'private' | 'trip';
  shares?: { user_id: string; share_minor: number }[] | null;
}

export interface UploadMeta { title?: string; category: string; sensitivity: 'standard' | 'identity'; tripId: string | null; identityNoticeAccepted?: boolean }

export interface InvitationPreview { state: 'valid' | 'invalid' | 'revoked' | 'expired' | 'used' | 'wrong_account' | 'already_member'; group_name?: string; invited_by?: string; role?: string; requires_approval?: boolean; expires_at?: string; group_id?: string }

export class DataError extends Error {
  constructor(public code: ErrorCode, message: string, public detail?: string) { super(message); }
}

export interface DataSource {
  readonly kind: 'supabase' | 'demo';
  // session
  getSession(): Promise<Session | null>;
  onAuthChange(cb: (s: Session | null) => void): () => void;
  signInWithEmail(email: string, redirectTo: string): Promise<void>;
  /** The code from the sign-in email, typed on any device. */
  verifyEmailCode(email: string, code: string): Promise<void>;
  /** The link from the sign-in email (token hash), opened on any device or browser. */
  verifyEmailLink(tokenHash: string, type: 'email' | 'magiclink' | 'signup'): Promise<void>;
  signOut(): Promise<void>;
  getConfig(): Promise<AppConfig>;
  // me
  getMe(): Promise<{ profile: Profile; settings: UserSettings }>;
  updateProfile(patch: Partial<Pick<Profile, 'display_name' | 'avatar_color'>>): Promise<void>;
  updateSettings(patch: Partial<Pick<UserSettings, 'home_city' | 'home_timezone' | 'reporting_currency' | 'locale' | 'ai_processing_consent'>>): Promise<void>;
  // trips
  listTrips(): Promise<(Trip & { myStatus: Enums['participation_status'] | null })[]>;
  getTrip(id: string): Promise<TripBundle>;
  createTrip(input: NewTripInput): Promise<string>;
  updateTrip(id: string, version: number, patch: Partial<Trip>): Promise<void>;
  saveStops(tripId: string, stops: (StopInput & { id?: string })[]): Promise<void>;
  deleteTrip(id: string): Promise<void>;
  respondTripInvitation(tripId: string, accept: boolean): Promise<void>;
  // events
  createEvent(input: EventInput, participantIds?: string[]): Promise<void>;
  updateEvent(id: string, version: number, patch: Partial<ItineraryEvent>, participantIds?: string[]): Promise<void>;
  deleteEvent(id: string): Promise<void>;
  reorderEvents(tripId: string, ids: string[]): Promise<void>;
  createManualBooking(tripId: string, event: EventInput, booking: Partial<Booking>): Promise<void>;
  listMyBookings(tripId: string): Promise<Booking[]>;
  /** Update your private booking details (optimistic: fails if it changed since it was loaded). */
  updateBooking(id: string, version: number, patch: Partial<Booking>): Promise<void>;
  /** Remove your booking record and its itinerary item. Never cancels anything with the provider. */
  deleteBooking(booking: Booking): Promise<void>;
  // attention
  listAttentionActions(tripId: string): Promise<AttentionAction[]>;
  recordAttention(tripId: string, fingerprint: string, action: Enums['attention_action'], reason?: string | null, snoozedUntil?: string | null): Promise<void>;
  // checklist
  listChecklist(tripId: string): Promise<ChecklistItem[]>;
  addChecklist(tripId: string, title: string, opts: { visibility: 'private' | 'trip'; due_on?: string | null; assignee_id?: string | null }): Promise<void>;
  setChecklistDone(item: ChecklistItem, done: boolean): Promise<void>;
  deleteChecklist(id: string): Promise<void>;
  // groups
  listGroups(): Promise<GroupSummary[]>;
  getGroup(id: string): Promise<GroupBundle>;
  createGroup(name: string, description: string | null): Promise<string>;
  createInvitation(groupId: string, input: { email: string | null; role: 'member' | 'admin'; expiresHours: number; maxUses: number }): Promise<{ link: string; email: 'sent' | 'not_requested' | 'not_configured' | 'failed' }>;
  revokeInvitation(id: string): Promise<void>;
  previewInvitation(token: string): Promise<InvitationPreview>;
  acceptInvitation(token: string): Promise<{ group_id: string; status: string }>;
  leaveGroup(groupId: string): Promise<void>;
  removeGroupMember(groupId: string, userId: string): Promise<void>;
  setGroupRole(groupId: string, userId: string, role: Enums['group_role']): Promise<void>;
  approveMember(groupId: string, userId: string, approve: boolean): Promise<void>;
  linkTripToGroup(tripId: string, version: number, groupId: string | null): Promise<void>;
  inviteToTrip(tripId: string, userIds: string[], role?: Enums['trip_role']): Promise<void>;
  setTripRole(tripId: string, userId: string, role: Enums['trip_role']): Promise<void>;
  removeFromTrip(tripId: string, userId: string): Promise<void>;
  createSubgroup(tripId: string, name: string, memberIds: string[]): Promise<void>;
  // documents
  listDocuments(): Promise<DocumentRow[]>;
  uploadDocument(file: File, meta: UploadMeta, onProgress?: (phase: 'reserving' | 'uploading' | 'checking') => void): Promise<string>;
  getDocumentUrl(doc: DocumentRow, download?: boolean): Promise<string>;
  renameDocument(id: string, title: string): Promise<void>;
  setDocumentTrip(id: string, tripId: string | null): Promise<void>;
  setDocumentCategory(id: string, category: DocumentRow['category']): Promise<void>;
  /** Elio reads a document and suggests its details. Nothing is saved. */
  readDocument(req: ReadDocumentRequest): Promise<DocumentReading>;
  /** Save the details the traveller confirmed (name, type, trip, route line, dates, provider, reference). */
  saveDocumentDetails(id: string, details: DocumentDetails): Promise<void>;
  deleteDocument(id: string): Promise<void>;
  // budget
  getBudget(tripId: string): Promise<BudgetBundle>;
  saveBudget(tripId: string, patch: Partial<TripBudget> & { currency: string }): Promise<void>;
  saveBudgetItem(tripId: string, item: Partial<BudgetItem> & { id?: string }): Promise<string>;
  deleteBudgetItem(id: string): Promise<void>;
  setAllocations(itemId: string, basis: Enums['allocation_basis'], rows: { user_id: string; share_minor?: number; percent?: number }[]): Promise<void>;
  setMyLimit(tripId: string, limit: { limit_minor: number; currency: string } | null): Promise<void>;
  addExpense(input: ExpenseInput): Promise<string>;
  voidExpense(id: string, reason: string): Promise<void>;
  recordSettlement(tripId: string, toUser: string, amountMinor: number, currency: string, note?: string): Promise<void>;
  respondSettlement(id: string, confirm: boolean): Promise<void>;
  // travel documents profile
  getTravelProfile(): Promise<{ profile: TravelDocProfile | null; credentials: TravelCredential[] }>;
  saveTravelProfile(patch: Pick<TravelDocProfile, 'nationalities' | 'residence_country'>): Promise<void>;
  saveCredential(c: Partial<TravelCredential> & { kind: TravelCredential['kind'] }): Promise<void>;
  deleteCredential(id: string): Promise<void>;
  listDestinations(): Promise<Destination[]>;
  listSavedDestinations(): Promise<string[]>;
  toggleSavedDestination(code: string, save: boolean): Promise<void>;
  // Elio conversations
  listConversations(): Promise<ConversationRow[]>;
  getConversation(id: string): Promise<{ conversation: ConversationRow; messages: ChatMessage[]; actions: ChatAction[] }>;
  sendChat(req: ChatRequest, onStatus?: (status: string) => void): Promise<ChatResponse>;
  decideAction(id: string, status: 'applied' | 'dismissed' | 'failed', result?: Record<string, unknown> | null, error?: string | null): Promise<void>;
  setConversationTrip(id: string, tripId: string): Promise<void>;
  deleteConversation(id: string): Promise<void>;
  listMemory(): Promise<MemoryRow[]>;
  deleteMemory(id: string): Promise<void>;
  // providers
  placesSearch(q: string, near?: string): Promise<{ status: string; message: string; deepLink?: { label: string; url: string } }>;
  exportAccount(): Promise<Blob>;
  /** Permanently close the signed-in account (server-side). */
  closeAccount(): Promise<void>;
}

export interface DocumentDetails {
  title: string; category: DocumentRow['category']; trip_id: string | null;
  subtitle: string | null; provider: string | null; reference: string | null;
  starts_on: string | null; ends_on: string | null; expires_on: string | null;
}
